import { Pencil, Upload as UploadOutlined, X, } from 'lucide-react';
import React from 'react';
import Cropper from 'react-easy-crop';
import { FormattedMessage, useIntl } from 'react-intl';
import { Slider } from '../../compat';
import { FileOutlined } from '../../icons';
import { useComponentDefaults } from '../../Provider';
import { clsx } from '../../utils/clsx';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import './MediaUpload.css';

const ensureArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const generateId = () => `media-${Math.random().toString(36).slice(2, 8)}-${Date.now().toString(36)}`;

const defaultAcceptByMediaType = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
  media: 'image/*,video/*,audio/*',
  file: '*/*',
};

const fileMatchesAccept = (file, accept) => {
  if (!accept) return true;
  const mimeType = String(file?.type || '').toLowerCase();
  const fileName = String(file?.name || '').toLowerCase();

  return accept.split(',').some((entry) => {
    const rule = entry.trim().toLowerCase();
    if (!rule) return false;
    if (rule.startsWith('.')) return fileName.endsWith(rule);
    if (rule.endsWith('/*')) return mimeType.startsWith(rule.slice(0, -1));
    return mimeType === rule;
  });
};

const shouldCreatePreview = (file, mediaType) => {
  if (!(file instanceof File)) return false;
  if (mediaType === 'video') return true;
  if (mediaType === 'audio') return false;
  if (mediaType === 'image') return true;
  const mimeType = file.type || '';
  if (mediaType === 'media') {
    return mimeType.startsWith('image/') || mimeType.startsWith('video/');
  }
  return mimeType.startsWith('image/');
};

const revokeObjectUrls = (urls) => {
  urls.forEach((url) => {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      // ignore revoke errors
    }
  });
};

const scheduleRevoke = (urls) => {
  if (!urls?.length) return;
  const run = () => revokeObjectUrls(urls);
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(run);
  } else {
    setTimeout(run, 0);
  }
};

const createNormalizedItem = (entry, mediaType) => {
  if (!entry) return null;
  const base = { ...entry };
  let file = null;
  let previewUrl = base.previewUrl || base._objectUrl;
  if (entry instanceof File) {
    file = entry;
    base.name = entry.name;
    base.size = entry.size;
    base.type = entry.type;
  } else if (entry.file instanceof File) {
    file = entry.file;
    base.name = base.name ?? entry.file.name;
    base.size = base.size ?? entry.file.size;
    base.type = base.type ?? entry.file.type;
  }

  let objectUrl;
  if (!previewUrl && shouldCreatePreview(file, mediaType)) {
    previewUrl = URL.createObjectURL(file);
    objectUrl = previewUrl;
  } else if (base._objectUrl) {
    objectUrl = base._objectUrl;
  }

  return {
    id: base.id || generateId(),
    name: base.name || '',
    size: base.size,
    type: base.type,
    file,
    previewUrl,
    _objectUrl: objectUrl,
    error: base.error,
    meta: {
      ...(base.meta || {}),
      mediaType,
    },
  };
};

const normalizeItems = (entries, mediaType) => {
  const normalized = [];
  ensureArray(entries).forEach((entry) => {
    const item = createNormalizedItem(entry, mediaType);
    if (item) normalized.push(item);
  });
  return normalized;
};

const iconForMedia = (kind) => {
  switch (kind) {
    default:
      return FileOutlined;
  }
};

const guessKindFromType = (type) => {
  if (!type) return 'file';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'file';
};

export const MediaUpload = React.forwardRef((rawProps, forwardedRef) => {
  const intl = useIntl();
  const merged = useComponentDefaults('MediaUpload', rawProps, {
    variant: 'surface',
    size: 'md',
    shape: 'default',
    color: 'brand',
    align: 'center',
  });

  const {
    as: RootComponent = 'section',
    variant = 'surface',
    size = 'md',
    shape = 'default',
    color = 'brand',
    align = 'center',
    className,
    style,
    slotProps: slotPropsProp,
    accept,
    mediaType = 'media',
    multiple = true,
    maxFiles,
    maxFileSize,
    allowDuplicates = false,
    defaultValue,
    value,
    disabled = false,
    readOnly = false,
    capture,
    name,
    id: providedId,
    helperText,
    description,
    label,
    showDropzone = true,
    showPreview = true,
    renderTrigger,
    renderEmpty,
    renderPreviewItem,
    children,
    strings = {},
    onChange,
    onFilesSelected,
    onValidationError,
    onRemove,
    dragAndDrop = true,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    ...rest
  } = merged;

  const dataAttributes = Object.keys(rest || {}).reduce((acc, key) => {
    if (key.startsWith('data-') || key.startsWith('aria-')) {
      acc[key] = rest[key];
    }
    return acc;
  }, {});

  const slotProps = React.useMemo(() => slotPropsProp || {}, [slotPropsProp]);
  const isControlled = value != null;

  const previewRegistry = React.useRef([]);
  const inputRef = React.useRef(null);
  const preventDoubleOpenRef = React.useRef(false);
  const resetOpenTimeoutRef = React.useRef(null);
  const [editingItem, setEditingItem] = React.useState(null);
  const editingTempUrlRef = React.useRef(null);
  const [editingSource, setEditingSource] = React.useState(null);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [savingEdit, setSavingEdit] = React.useState(false);
  const combinedRef = React.useCallback(
    (node) => {
      inputRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef]
  );
  const [items, setItemsState] = React.useState(() => {
    const initialEntries = isControlled ? value : defaultValue;
    const normalized = normalizeItems(initialEntries, mediaType);
    previewRegistry.current = normalized
      .filter((item) => item._objectUrl)
      .map((item) => item._objectUrl);
    return normalized;
  });

  React.useEffect(() => () => {
    revokeObjectUrls(previewRegistry.current);
    previewRegistry.current = [];
  }, []);

  const syncFromExternal = React.useCallback(
    (entries) => {
      const normalized = normalizeItems(entries, mediaType);
      const nextObjectUrls = normalized
        .filter((item) => item._objectUrl)
        .map((item) => item._objectUrl);
      const previousUrls = previewRegistry.current;
      previewRegistry.current = nextObjectUrls;
      setItemsState(normalized);
      const toRevoke = previousUrls.filter((url) => !nextObjectUrls.includes(url));
      scheduleRevoke(toRevoke);
    },
    [mediaType]
  );

  React.useEffect(() => {
    if (isControlled) {
      syncFromExternal(value);
    }
  }, [isControlled, value, syncFromExternal]);

  const enforceLimits = React.useCallback(
    (incomingFiles, currentItems) => {
      const next = [];
      const existingSignatures = new Set(
        currentItems.map((item) => `${item.name}:${item.size}:${item.type}`)
      );
      const computedAccept = accept || defaultAcceptByMediaType[mediaType] || undefined;
      for (const file of incomingFiles) {
        if (!multiple && (currentItems.length > 0 || next.length > 0)) break;
        if (maxFiles && currentItems.length + next.length >= maxFiles) {
          onValidationError?.({ type: 'maxFiles', file, maxFiles });
          break;
        }
        if (!fileMatchesAccept(file, computedAccept)) {
          onValidationError?.({ type: 'fileType', file, accept: computedAccept });
          continue;
        }
        if (!allowDuplicates) {
          const signature = `${file.name}:${file.size}:${file.type}`;
          if (existingSignatures.has(signature)) continue;
          existingSignatures.add(signature);
        }
        if (maxFileSize && file.size > maxFileSize) {
          onValidationError?.({ type: 'fileSize', file, maxFileSize });
          continue;
        }
        next.push(file);
      }
      return next;
    },
    [accept, allowDuplicates, maxFileSize, maxFiles, mediaType, multiple, onValidationError]
  );

  const handleFiles = React.useCallback(
    (fileList) => {
      if (!fileList?.length) return;
      const files = enforceLimits(Array.from(fileList), items);
      if (!files.length) return;
      const newItems = normalizeItems(files, mediaType);
      if (newItems.length) {
        previewRegistry.current = [
          ...previewRegistry.current,
          ...newItems.filter((item) => item._objectUrl).map((item) => item._objectUrl),
        ];
      }
      const nextItems = [...items, ...newItems];
      setItemsState(nextItems);
      onChange?.(nextItems, { action: 'add', files });
      onFilesSelected?.(files, nextItems);
    },
    [enforceLimits, items, mediaType, onChange, onFilesSelected]
  );

  const handleInputChange = React.useCallback(
    (event) => {
      handleFiles(event.target.files);
      // Reset the input so the same file can be selected again if needed.
      event.target.value = '';
    },
    [handleFiles]
  );

  const removeItem = React.useCallback(
    (id) => {
      const next = items.filter((item) => item.id !== id);
      const removed = items.find((item) => item.id === id);
      if (!removed) return;
      let urlsToRevoke = [];
      if (removed._objectUrl) {
        urlsToRevoke = [removed._objectUrl];
        previewRegistry.current = previewRegistry.current.filter((url) => url !== removed._objectUrl);
      }
      setItemsState(next);
      onChange?.(next, { action: 'remove', item: removed });
      onRemove?.(removed, next);
      scheduleRevoke(urlsToRevoke);
    },
    [items, onChange, onRemove]
  );

  React.useEffect(
    () => () => {
      if (resetOpenTimeoutRef.current) {
        clearTimeout(resetOpenTimeoutRef.current);
        resetOpenTimeoutRef.current = null;
      }
    },
    []
  );

  const requestFileDialog = React.useCallback(() => {
    if (disabled || readOnly) return;
    if (preventDoubleOpenRef.current) return;
    preventDoubleOpenRef.current = true;
    if (resetOpenTimeoutRef.current) {
      clearTimeout(resetOpenTimeoutRef.current);
    }
    resetOpenTimeoutRef.current = setTimeout(() => {
      preventDoubleOpenRef.current = false;
      resetOpenTimeoutRef.current = null;
    }, 0);
    inputRef.current?.click();
  }, [disabled, readOnly]);

  const triggerOpen = React.useCallback(() => {
    requestFileDialog();
  }, [requestFileDialog]);

  const handleDropzoneClick = React.useCallback(() => {
    requestFileDialog();
  }, [requestFileDialog]);

  const [dragActive, setDragActive] = React.useState(false);

  const onDragOver = React.useCallback(
    (event) => {
      if (!dragAndDrop) return;
      event.preventDefault();
      if (disabled || readOnly) return;
      event.dataTransfer.dropEffect = 'copy';
      setDragActive(true);
    },
    [dragAndDrop, disabled, readOnly]
  );

  const onDragLeave = React.useCallback(() => setDragActive(false), []);

  const onDrop = React.useCallback(
    (event) => {
      if (!dragAndDrop) return;
      event.preventDefault();
      setDragActive(false);
      if (disabled || readOnly) return;
      handleFiles(event.dataTransfer.files);
    },
    [dragAndDrop, disabled, readOnly, handleFiles]
  );

  const computedAccept = accept || defaultAcceptByMediaType[mediaType] || undefined;
  const inputId = React.useId();
  const controlId = providedId || inputId;
  const hasCustomContent = React.Children.count(children) > 0;

  const resolvedAriaLabel = React.useMemo(() => {
    if (ariaLabel) return ariaLabel;
    if (typeof label === 'string') return label;
    try {
      return intl.formatMessage(
        { id: 'designSystem.mediaUpload.triggerLabel', defaultMessage: 'Select files' },
        {
          mediaType: intl.formatMessage(
            { id: `designSystem.mediaUpload.type.${mediaType}`, defaultMessage: mediaType }
          ),
        }
      );
    } catch (error) {
      return 'Select files';
    }
  }, [ariaLabel, intl, label, mediaType]);

  const renderDefaultTrigger = () => (
    <Button
      type="button"
      variant="filled"
      color={color === 'brand' ? 'primary' : color}
      className="ds-media-upload__trigger"
      disabled={disabled || readOnly}
      onClick={triggerOpen}
      {...slotProps.trigger}
    >
      <UploadOutlined aria-hidden size={16} />
      <span>
        {strings.triggerLabel ? (
          typeof strings.triggerLabel === 'function' ? (
            strings.triggerLabel({ mediaType, items })
          ) : (
            strings.triggerLabel
          )
        ) : (
          <FormattedMessage
            id="designSystem.mediaUpload.triggerLabel"
            defaultMessage="Select files"
            values={{
              mediaType: intl.formatMessage(
                { id: `designSystem.mediaUpload.type.${mediaType}`, defaultMessage: mediaType }
              ),
            }}
          />
        )}
      </span>
    </Button>
  );

  const dropHint = strings.dropHint ? (
    typeof strings.dropHint === 'function' ? (
      strings.dropHint({ mediaType, items })
    ) : (
      strings.dropHint
    )
  ) : (
    <FormattedMessage
      id="designSystem.mediaUpload.dropHint"
      defaultMessage="Drag & drop to upload or browse files"
    />
  );

  const dragActiveLabel = strings.dropActive ? (
    typeof strings.dropActive === 'function' ? (
      strings.dropActive({ mediaType, items })
    ) : (
      strings.dropActive
    )
  ) : (
    <FormattedMessage
      id="designSystem.mediaUpload.dropActive"
      defaultMessage="Release to upload"
    />
  );

  const renderThumbnail = (item) => {
    const kind = mediaType === 'media' ? guessKindFromType(item.type) : mediaType;
    if (item.previewUrl && (kind === 'image' || kind === 'video')) {
      if (kind === 'video') {
        return (
          <video
            className="ds-media-upload__preview-media ds-media-upload__preview-video"
            src={item.previewUrl}
            aria-label={intl.formatMessage(
              { id: 'designSystem.mediaUpload.previewVideoLabel', defaultMessage: 'Preview of {name}' },
              { name: item.name || '' }
            )}
            controls
            muted
            playsInline
            preload="metadata"
          />
        );
      }
      return (
        <img
          className="ds-media-upload__preview-media"
          src={item.previewUrl}
          alt={intl.formatMessage(
            { id: 'designSystem.mediaUpload.previewImageAlt', defaultMessage: 'Preview of {name}' },
            { name: item.name || '' }
          )}
        />
      );
    }
    const Icon = iconForMedia(kind);
    return <Icon className="ds-media-upload__preview-fallback" aria-hidden />;
  };

  const renderList = () => {
    if (!items.length) {
      if (renderEmpty) {
        return renderEmpty({ items, mediaType });
      }
      return null;
    }

    return (
      <div className="ds-media-upload__grid" {...slotProps.list}>
        {items.map((item) => {
          const kind = mediaType === 'media' ? guessKindFromType(item.type) : mediaType;
          if (renderPreviewItem) {
            const removeHandler = () => removeItem(item.id);
            const editHandler = !readOnly && kind === 'image' ? () => setEditingItem(item) : undefined;
            return (
              <div key={item.id} className="ds-media-upload__grid-item" {...slotProps.item}>
                {renderPreviewItem({
                  item,
                  kind,
                  readOnly,
                  remove: removeHandler,
                  edit: editHandler,
                  slotProps: {
                    remove: slotProps.remove,
                    edit: slotProps.edit,
                    preview: slotProps.preview,
                  },
                })}
              </div>
            );
          }
          const removeLabel = strings.removeFileLabel
            ? typeof strings.removeFileLabel === 'function'
              ? strings.removeFileLabel({ item })
              : strings.removeFileLabel
            : intl.formatMessage(
                { id: 'designSystem.mediaUpload.removeFile', defaultMessage: 'Remove {name}' },
                { name: item.name || '' }
              );
          return (
            <div key={item.id} className="ds-media-upload__grid-item" {...slotProps.item}>
              {showPreview && (
                <div className="ds-media-upload__preview" data-kind={kind}>
                  {renderThumbnail(item)}
                </div>
              )}
              {!readOnly && (
                <Button
                  size="small"
                  variant="filled"
                  color="var(--surface-3)"
                  style={{ color: 'var(--text)' }}
                  className="ds-media-upload__remove"
                  onClick={() => removeItem(item.id)}
                  {...slotProps.remove}
                  leftIcon={<X size={14} />}
                />
              )}
              {!readOnly && kind === 'image' && (
                <Button
                  size="small"
                  variant="filled"
                  color="var(--surface-3)"
                  style={{ color: 'var(--text)' }}
                  className="ds-media-upload__edit"
                  onClick={() => setEditingItem(item)}
                  leftIcon={<Pencil size={14} />}
                  {...slotProps.edit}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const closeEditing = React.useCallback(() => {
    setEditingItem(null);
  }, []);

  const loadEditingSource = React.useCallback(
    async (item) => {
      if (!item) return null;
      let source = item.previewUrl || item.url || null;
      let tempUrl = null;
      if (!source && item.file instanceof File) {
        tempUrl = URL.createObjectURL(item.file);
        source = tempUrl;
      }
      if (!source && item.file && item.file.preview) {
        source = item.file.preview;
      }
      if (!source && item.file && typeof item.file === 'object' && item.file.url) {
        source = item.file.url;
      }
      if (!source && item.url) {
        try {
          const response = await fetch(item.url);
          const blob = await response.blob();
          tempUrl = URL.createObjectURL(blob);
          source = tempUrl;
        } catch (error) {
          console.error('MediaUpload: failed to load image for cropping', error);
        }
      }
      return { source, tempUrl };
    },
    []
  );

  React.useEffect(() => {
    let cancelled = false;
    const prepare = async () => {
      if (!editingItem) {
        if (editingTempUrlRef.current) {
          URL.revokeObjectURL(editingTempUrlRef.current);
          editingTempUrlRef.current = null;
        }
        setEditingSource(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        return;
      }

      if (!editingItem.type || !editingItem.type.startsWith('image')) {
        closeEditing();
        return;
      }

      const { source, tempUrl } = await loadEditingSource(editingItem);
      if (cancelled) {
        if (tempUrl) URL.revokeObjectURL(tempUrl);
        return;
      }
      if (!source) {
        closeEditing();
        return;
      }

      if (editingTempUrlRef.current) {
        URL.revokeObjectURL(editingTempUrlRef.current);
        editingTempUrlRef.current = null;
      }
      if (tempUrl) {
        editingTempUrlRef.current = tempUrl;
      }
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      console.log('source', source);

      setEditingSource(source);
    };

    prepare();
    return () => {
      cancelled = true;
    };
  }, [editingItem, closeEditing, loadEditingSource]);

  React.useEffect(() => () => {
    if (editingTempUrlRef.current) {
      URL.revokeObjectURL(editingTempUrlRef.current);
      editingTempUrlRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!editingItem) return;
    const exists = items.some((item) => item.id === editingItem.id);
    if (!exists) {
      closeEditing();
    }
  }, [items, editingItem, closeEditing]);

  const onCropComplete = React.useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const createCroppedFile = React.useCallback(async () => {
    if (!editingSource || !croppedAreaPixels || !editingItem) return null;
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = editingSource;
    });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const width = Math.max(1, Math.round(croppedAreaPixels.width));
    const height = Math.max(1, Math.round(croppedAreaPixels.height));
    canvas.width = width;
    canvas.height = height;
    context.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      width,
      height
    );
    const mimeType = editingItem.type && editingItem.type.startsWith('image/')
      ? editingItem.type
      : 'image/jpeg';
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, 0.92));
    if (!blob) return null;
    const extensionMatch = (editingItem.name || '').match(/\.[a-zA-Z0-9]+$/);
    const defaultName = extensionMatch ? editingItem.name : `${editingItem.name || 'image'}.jpg`;
    const croppedFile = new File([blob], defaultName, { type: mimeType, lastModified: Date.now() });
    const objectUrl = URL.createObjectURL(croppedFile);
    return { file: croppedFile, objectUrl };
  }, [croppedAreaPixels, editingItem, editingSource]);

  const handleSaveCrop = React.useCallback(async () => {
    if (!editingItem) return;
    setSavingEdit(true);
    try {
      const result = await createCroppedFile();
      if (!result) {
        setSavingEdit(false);
        return;
      }
      const { file: croppedFile, objectUrl } = result;
      let updatedItem = null;
      let nextItems = null;
      setItemsState((prev) => {
        const next = prev.map((item) => {
          if (item.id !== editingItem.id) return item;
          if (item._objectUrl && item._objectUrl !== objectUrl) {
            URL.revokeObjectURL(item._objectUrl);
            previewRegistry.current = previewRegistry.current.filter((url) => url !== item._objectUrl);
          }
          updatedItem = {
            ...item,
            file: croppedFile,
            originFileObj: croppedFile,
            previewUrl: objectUrl,
            _objectUrl: objectUrl,
            size: croppedFile.size,
            type: croppedFile.type,
          };
          return updatedItem;
        });
        previewRegistry.current = [...previewRegistry.current.filter(Boolean), objectUrl];
        nextItems = next;
        return next;
      });
      if (nextItems && updatedItem) {
        onChange?.(nextItems, { action: 'edit', item: updatedItem, file: croppedFile });
      }
      if (editingTempUrlRef.current) {
        URL.revokeObjectURL(editingTempUrlRef.current);
        editingTempUrlRef.current = null;
      }
      setEditingSource(null);
      setEditingItem(null);
    } catch (error) {
      console.error('MediaUpload: failed to crop image', error);
    } finally {
      setSavingEdit(false);
    }
  }, [createCroppedFile, editingItem, onChange]);

  const rootClasses = clsx(
    'ds-media-upload',
    `ds-media-upload--variant-${variant}`,
    `ds-media-upload--size-${size}`,
    `ds-media-upload--shape-${shape}`,
    color && `ds-media-upload--color-${color}`,
    align && `ds-media-upload--align-${align}`,
    disabled && 'ds-media-upload--disabled',
    className
  );

  const dropzoneClasses = clsx(
    'ds-media-upload__dropzone',
    dragActive && 'ds-media-upload__dropzone--dragging',
    (disabled || readOnly) && 'ds-media-upload__dropzone--disabled'
  );

  const hint = dragActive ? dragActiveLabel : dropHint;

  const renderDropzoneContent = () => {
    if (!hasCustomContent) {
      return (
        <>
          <div className="ds-media-upload__drop-icon" aria-hidden>
            <UploadOutlined size={16} />
          </div>
          <div className="ds-media-upload__drop-content">
            {renderTrigger ? renderTrigger({ triggerOpen, items, mediaType }) : renderDefaultTrigger()}
            <p className="ds-media-upload__hint">{hint}</p>
          </div>
        </>
      );
    }

    if (typeof children === 'function') {
      return children({ triggerOpen, items, mediaType, dragActive, disabled, readOnly });
    }

    return children;
  };

  return (
    <RootComponent className={rootClasses} style={style} {...dataAttributes}>
      {label && !hasCustomContent && (
        <label className="ds-media-upload__label" htmlFor={controlId}>
          {label}
        </label>
      )}
      {description && !hasCustomContent && <div className="ds-media-upload__description">{description}</div>}
      {showDropzone && (
        <div
          className={dropzoneClasses}
          role="button"
          tabIndex={disabled || readOnly ? -1 : 0}
          onClick={handleDropzoneClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              triggerOpen();
            }
          }}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          aria-disabled={disabled || readOnly}
          {...slotProps.dropzone}
        >
          {renderDropzoneContent()}
        </div>
      )}
      {helperText && <div className="ds-media-upload__helper">{helperText}</div>}
      <input
        id={controlId}
        name={name}
        ref={combinedRef}
        type="file"
        className="ds-media-upload__input"
        accept={computedAccept}
        multiple={multiple}
        disabled={disabled || readOnly}
        onChange={handleInputChange}
        capture={capture}
        aria-label={resolvedAriaLabel}
        aria-describedby={ariaDescribedBy}
        {...slotProps.input}
      />
      {showPreview && renderList()}
      <Modal
        open={Boolean(editingItem)}
        onCancel={closeEditing}
        onClose={closeEditing}
        destroyOnHidden
        fullscreen="mobile"
        width={700}
        title={<FormattedMessage id="crop_image" defaultMessage="Crop image" />}
        className="ds-media-upload__crop-modal"
        styles={{
          body: { padding: 0 },
          content: { padding: 0 },
        }}
        style={{width: 600}}
        onOk={handleSaveCrop}
        okText={intl.formatMessage({ id: 'auth_save', defaultMessage: 'Save' })}

      >
        {editingSource ? (
          <div className="ds-media-upload__crop-wrapper">
            <div className="ds-media-upload__cropper">
              <Cropper
                image={editingSource}
                crop={crop}
                zoom={zoom}
                aspect={18 / 9}
                onCropChange={setCrop}
                onZoomChange={(value) => {
                  if (typeof value === 'number') {
                    setZoom(value);
                  }
                }}
                onCropComplete={onCropComplete}
                restrictPosition
                 showGrid={false}
              />
            </div>
            <div className="ds-media-upload__crop-controls">
              <span className="ds-media-upload__crop-label">
                <FormattedMessage id="designSystem.mediaUpload.zoom" defaultMessage="Zoom" />
              </span>
              <Slider
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(value) => {
                  if (typeof value === 'number') {
                    setZoom(value);
                  }
                }}
                aria-label={intl.formatMessage({ id: 'designSystem.mediaUpload.zoom', defaultMessage: 'Zoom' })}
              />
            </div>
          </div>
        ) : editingItem ? (
          <div className="ds-media-upload__crop-placeholder">
            <FormattedMessage id="loading" defaultMessage="Loading…" />
          </div>
        ) : null}
      </Modal>
    </RootComponent>
  );
});

MediaUpload.displayName = 'DesignSystemMediaUpload';

export default MediaUpload;
